export type LeadSelectFields = {
  lead_title?: string | null;
  name?: string | null;
  display_id?: string | null;
  created_at?: string | null;
  /** Kunden-Bestellnummer / Auftragskennzeichen (Baustelle). */
  bestellnummer?: string | null;
  /** Anzeigename des Baustellenkontakts. */
  site_contact_name?: string | null;
  /** Baustellenadresse / Objektadresse. */
  site_address?: string | null;
};

function contactNameFromParts(c: {
  name?: unknown;
  first_name?: unknown;
  last_name?: unknown;
} | null | undefined): string {
  if (!c) return '';
  const combined = [c.first_name, c.last_name]
    .map((p) => String(p ?? '').trim())
    .filter(Boolean)
    .join(' ')
    .trim();
  if (combined) return combined;
  return String(c.name ?? '').trim();
}

function trimOrNull(v: unknown): string | null {
  const s = String(v ?? '').trim();
  return s || null;
}

/** Bestellnummer, Baustellenkontakt und Adresse aus GET /api/leads (inkl. `baustelle`). */
export function leadSelectSiteFieldsFromApi(lead: {
  baustelle?: {
    bestellnummer?: unknown;
    address?: unknown;
    contact?: { name?: unknown; first_name?: unknown; last_name?: unknown } | null;
  } | null;
  bestellnummer?: unknown;
  site_contact_name?: unknown;
  site_address?: unknown;
}): Pick<LeadSelectFields, 'bestellnummer' | 'site_contact_name' | 'site_address'> {
  const bestellnummer =
    lead.baustelle?.bestellnummer != null
      ? String(lead.baustelle.bestellnummer).trim() || null
      : lead.bestellnummer != null
        ? String(lead.bestellnummer).trim() || null
        : null;
  const fromSite = contactNameFromParts(lead.baustelle?.contact ?? null);
  const site_contact_name =
    fromSite || (lead.site_contact_name != null ? String(lead.site_contact_name).trim() || null : null);
  const site_address = trimOrNull(lead.baustelle?.address) ?? trimOrNull(lead.site_address);
  return { bestellnummer, site_contact_name, site_address };
}

/** Primary line for project pickers: title first, then contact, then id. */
export function leadSelectPrimaryLabel(lead: LeadSelectFields): string {
  const title = String(lead.lead_title ?? '').trim();
  if (title) return title;
  const name = String(lead.name ?? '').trim();
  if (name) return name;
  const id = String(lead.display_id ?? '').trim();
  return id || 'Projekt';
}

/** Secondary line: Ansprechpartner · ID · Baustellenadresse (omit parts already used as primary). */
export function leadSelectSecondaryLabel(lead: LeadSelectFields): string | undefined {
  const title = String(lead.lead_title ?? '').trim();
  const name = String(lead.name ?? '').trim();
  const id = String(lead.display_id ?? '').trim();
  const address = String(lead.site_address ?? '').trim();
  const parts: string[] = [];
  if (title && name) parts.push(name);
  if (id && (title || name !== id)) parts.push(id);
  if (address) parts.push(address);
  if (parts.length === 0) return undefined;
  return parts.join(' · ');
}

/** Search-result secondary line: always keep the three slots, with empty labels. */
export function leadSelectSearchSecondaryLabel(lead: LeadSelectFields): string {
  const title = String(lead.lead_title ?? '').trim();
  const name = String(lead.name ?? '').trim();
  const id = String(lead.display_id ?? '').trim();
  const address = String(lead.site_address ?? '').trim();
  const parts: string[] = [];
  if (title) parts.push(name || 'Kein Kunde');
  parts.push(id || 'Keine Projektnummer');
  parts.push(address || 'Keine Adresse');
  return parts.join(' · ');
}

/** Freitext für die Projektsuche: Titel, ID, Ansprechpartner, Bestellnummer, Baustellenkontakt, Adresse. */
export function leadSelectSearchText(lead: LeadSelectFields): string {
  return [
    lead.lead_title,
    lead.name,
    lead.display_id,
    lead.bestellnummer,
    lead.site_contact_name,
    lead.site_address,
  ]
    .map((v) => String(v ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

export function matchesLeadSelectQuery(lead: LeadSelectFields, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    lead.lead_title,
    lead.name,
    lead.display_id,
    lead.bestellnummer,
    lead.site_contact_name,
    lead.site_address,
  ].some((v) => String(v ?? '').toLowerCase().includes(q));
}

/** Optionen für `AppSelectDropdown` (Plantafel / E-Mail / Aktivitäten). */
export function leadSelectDropdownFields(lead: LeadSelectFields): {
  label: string;
  description?: string;
  searchText: string;
} {
  return {
    label: leadSelectPrimaryLabel(lead),
    description: leadSelectSecondaryLabel(lead),
    searchText: leadSelectSearchText(lead),
  };
}
